/**
 * El informe del asistente en Excel (.xlsx).
 *
 * Sale de la MISMA especificación que el PDF (AssistantDocSpec): título,
 * empresa, filtros, cifras clave y la tabla, con las mismas columnas y filas
 * (las columnas `hideIfEmpty` vacías se quitan igual que en el PDF). Lo que
 * cambia es que en Excel las cifras tienen que ser NÚMEROS, o no se pueden
 * sumar ni ordenar: las filas llegan escritas en el idioma de quien preguntó
 * («1.234,50 €», «12,5 %»), y aquí se vuelven número con los separadores de
 * ESE idioma (`doc.locale`), nunca adivinándolos. Lo que no se lee con total
 * seguridad se queda como texto: mejor una celda de texto que un número falso.
 *
 * SheetJS se carga solo al pulsar «Excel» (import dinámico): pesa y la mayoría
 * de los turnos del chat no lo necesitan.
 */

import type { AssistantDocSpec } from "@/hooks/useAssistantStream";
import { buildExportFilename } from "@/components/export/useExportPdf";
import { openFileInNativeViewer } from "@/lib/pdfDelivery";
import { isDespiaNative } from "@/lib/isDespiaNative";
import type { MarcaInformes } from "@/lib/marca/tipos";
import { aplicarMarcaXlsx } from "@/lib/marca/xlsxMarca";

export const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** Una cifra leída de su texto: el número y cómo enseñarla en Excel. */
export interface ParsedCell {
  v: number;
  /** Formato de número de Excel (z). */
  z: string;
}

const BIDI_MARKS = /[\u200e\u200f\u061c]/g;
const SPACES = /[\s\u00a0\u202f]/g;
const SPACE_CHAR = /^[\s\u00a0\u202f]$/;
const GROUP = "\u0001";

function separatorsOf(locale: string): { group: string; decimal: string } | null {
  try {
    const parts = new Intl.NumberFormat(locale, { useGrouping: true }).formatToParts(12345.6);
    const group = parts.find((p) => p.type === "group")?.value;
    const decimal = parts.find((p) => p.type === "decimal")?.value;
    return group && decimal ? { group, decimal } : null;
  } catch {
    return null;
  }
}

/**
 * Lee una cifra escrita en `locale` («-2.616,42 €», «12,5 %», «1 234»). Devuelve
 * null ante cualquier duda: letras (unidades como «12 L»), grupos de miles mal
 * puestos, dos decimales… El texto se queda como está.
 */
export function parseLocaleNumber(text: string, locale: string | undefined): ParsedCell | null {
  if (!locale) return null;
  const sep = separatorsOf(locale);
  if (!sep) return null;
  let s = text.replace(BIDI_MARKS, "").trim();
  if (!s) return null;

  let kind: "number" | "percent" | "money" = "number";
  let symbol = "";
  let symbolFirst = false;
  if (s.includes("%")) {
    kind = "percent";
    s = s.replace(/%/g, "").trim();
  } else {
    const m = s.match(/\p{Sc}/u);
    if (m) {
      kind = "money";
      symbol = m[0];
      symbolFirst = s.indexOf(m[0]) < s.search(/\d/);
      s = s.replace(/\p{Sc}/gu, "").trim();
    }
  }

  let negative = false;
  if (/^[-−]/.test(s)) {
    negative = true;
    s = s.slice(1).trim();
  }

  // Los miles a una marca propia: si el idioma los separa con espacio, cualquier espacio.
  const body = SPACE_CHAR.test(sep.group) ? s.replace(SPACES, GROUP) : s.split(sep.group).join(GROUP);
  const di = body.indexOf(sep.decimal);
  const intPart = di >= 0 ? body.slice(0, di) : body;
  const fracPart = di >= 0 ? body.slice(di + sep.decimal.length) : "";
  if (!/^\d+$/.test(intPart) && !new RegExp(`^\\d{1,3}(${GROUP}\\d{3})+$`).test(intPart)) return null;
  if (di >= 0 && !/^\d+$/.test(fracPart)) return null;

  const n = Number(`${intPart.split(GROUP).join("")}${fracPart ? `.${fracPart}` : ""}`);
  if (!Number.isFinite(n)) return null;
  const v = negative ? -n : n;
  const decimals = fracPart.length;
  const num = decimals > 0 ? `#,##0.${"0".repeat(decimals)}` : "#,##0";

  // «47,2 %» → 0.472 leyendo el texto con el exponente (v / 100 daba 0.47200000000000003).
  if (kind === "percent") return { v: Number(`${v}e-2`), z: decimals > 0 ? `0.${"0".repeat(decimals)}%` : "0%" };
  if (kind === "money") {
    const sym = `"${symbol.replace(/"/g, "")}"`;
    return { v, z: symbolFirst ? `${sym}${num}` : `${num} ${sym}` };
  }
  return { v, z: num };
}

type Cell = string | ParsedCell | null;

export interface ReportSheet {
  /** Filas de la hoja, arriba abajo: cabecera del informe, hueco, tabla. */
  rows: Cell[][];
  /** Índice (0) de la fila con los títulos de columna. */
  headerRow: number;
  /** Número de columnas de la tabla. */
  tableCols: number;
  /** Ancho de cada columna, en caracteres. */
  widths: number[];
  /** Cuántas tablas lleva la hoja (con más de una no se pone autofiltro: Excel solo admite uno). */
  sections: number;
}

/** Vacía en todas las filas, como decide el PDF (buildReportPdf) para quitarla. */
const blank = (v: unknown) => v == null || v === "" || v === "—";

/**
 * La hoja como matriz, PURA (sin SheetJS): cabecera con título, empresa,
 * filtros y cifras clave; una fila en blanco; y la tabla. Las columnas a la
 * derecha (las de cifras) y los valores de las cifras clave se leen como número.
 */
export function reportSheet(doc: AssistantDocSpec, companyName: string, lineasMarca: string[] = []): ReportSheet {
  const asCell = (text: string, tryNumber: boolean): Cell =>
    (tryNumber ? parseLocaleNumber(text, doc.locale) : null) ?? text;

  const rows: Cell[][] = [[doc.title]];
  if (companyName) rows.push([companyName]);
  // «Personalizar informes» (29-09-2026): las líneas que la empresa enseña
  // (NIF, dirección…), debajo de su nombre. Sin marca, ninguna.
  for (const l of lineasMarca) if (l.trim()) rows.push([l]);

  /* Informe compuesto (28-09-2026): cada sección con su título, sus filtros y
     cifras y su tabla, una debajo de otra en la MISMA hoja («que te cree otro
     Excel debajo, dentro de ese mismo»), con una fila en blanco entre medias. */
  const secciones = doc.sections?.length
    ? doc.sections
    : [{ title: "", filters: doc.filters, kpis: doc.kpis, columns: doc.columns, rows: doc.rows }];
  const widths: number[] = [];
  let headerRow = -1;
  let tableCols = 0;
  secciones.forEach((sec, n) => {
    const keep = sec.columns.map((c, i) => !c.hideIfEmpty || sec.rows.some((r) => !blank(r[i])));
    const cols = sec.columns.filter((_, i) => keep[i]);
    const numeric = cols.map((c) => c.align === "right");
    if (n > 0) rows.push([]);
    if (sec.title) rows.push([sec.title]);
    for (const f of sec.filters ?? []) rows.push([f.label, f.value]);
    for (const k of sec.kpis ?? []) rows.push([k.label, asCell(k.value, true)]);
    rows.push([]);
    if (n === 0) {
      headerRow = rows.length;
      tableCols = cols.length;
    }
    rows.push(cols.map((c) => c.header));
    for (const r of sec.rows) {
      const kept = r.filter((_, i) => keep[i]);
      rows.push(kept.map((v, i) => (v == null || v === "" ? null : asCell(String(v), numeric[i]))));
    }
    cols.forEach((c, i) => {
      let w = c.header.length;
      for (const r of sec.rows) {
        const kept = r.filter((_, j) => keep[j]);
        w = Math.max(w, String(kept[i] ?? "").length);
      }
      widths[i] = Math.max(widths[i] ?? 0, Math.min(60, Math.max(10, w + 2)));
    });
    // La primera columna lleva también las etiquetas de filtros y cifras clave.
    if (widths.length > 0) {
      for (const f of [...(sec.filters ?? []), ...(sec.kpis ?? [])]) widths[0] = Math.min(60, Math.max(widths[0], f.label.length + 2));
    }
  });

  return { rows, headerRow, tableCols, widths, sections: secciones.length };
}

/** Nombre de hoja válido en Excel: sin []:*?/\ y como mucho 31 caracteres. */
export function sheetName(title: string): string {
  return title.replace(/[[\]:*?/\\]/g, " ").replace(/\s+/g, " ").trim().slice(0, 31) || "Report";
}

export function xlsxFilename(doc: AssistantDocSpec, prefijo?: string): string {
  return buildExportFilename(doc.report_id, doc.date_start, doc.date_end, prefijo).replace(/\.pdf$/, ".xlsx");
}

/**
 * El .xlsx del informe. Carga SheetJS en este momento, no antes. Con la marca de
 * la empresa, su nombre y sus líneas de cabecera arriba (el logo y los colores, en F1b).
 */
export async function buildReportXlsx(doc: AssistantDocSpec, companyName: string, marca: MarcaInformes | null = null): Promise<Blob> {
  const XLSX = await import("xlsx");
  const nombre = marca?.nombre || companyName;
  const sheet = reportSheet(doc, nombre, marca?.lineas ?? []);
  const ws: Record<string, unknown> = {};
  let maxCol = 0;
  sheet.rows.forEach((row, r) => {
    row.forEach((cell, c) => {
      if (cell == null) return;
      const ref = XLSX.utils.encode_cell({ r, c });
      ws[ref] = typeof cell === "string" ? { t: "s", v: cell } : { t: "n", v: cell.v, z: cell.z };
      maxCol = Math.max(maxCol, c);
    });
  });
  const lastRow = sheet.rows.length - 1;
  ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lastRow, c: Math.max(maxCol, sheet.tableCols - 1, sheet.widths.length - 1) } });
  ws["!cols"] = sheet.widths.map((wch) => ({ wch }));
  // Con logo, las filas del título un poco más altas: el logo va encima, a la derecha.
  if (marca?.logo) ws["!rows"] = [{ hpt: 26 }, { hpt: 18 }, { hpt: 16 }];
  if (sheet.tableCols > 0 && sheet.sections === 1) {
    ws["!autofilter"] = {
      ref: XLSX.utils.encode_range({ s: { r: sheet.headerRow, c: 0 }, e: { r: lastRow, c: sheet.tableCols - 1 } }),
    };
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws as never, sheetName(doc.title));
  wb.Props = { Title: doc.title, ...(nombre ? { Company: nombre } : {}) };
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array", compression: true }) as ArrayBuffer;
  if (!marca) return new Blob([out], { type: XLSX_MIME });
  // F1b: el logo y los colores (si algo falla, el mismo Excel con sus líneas de texto).
  const lineas = marca.lineas.filter((l) => l.trim()).length;
  const primeraLinea = nombre ? 2 : 1;
  const conMarca = aplicarMarcaXlsx(new Uint8Array(out), marca, {
    filaTitulo: 0,
    filaEmpresa: nombre ? 1 : null,
    filasLineas: Array.from({ length: lineas }, (_, i) => primeraLinea + i),
    filaCabecera: sheet.headerRow,
    columnas: sheet.tableCols,
    anchos: sheet.widths,
  });
  // Su ArrayBuffer (copia exacta: sale de `new Uint8Array(...)`); el TypeScript nuevo de Lovable no acepta el Uint8Array genérico en un Blob.
  return new Blob([conMarca.buffer as ArrayBuffer], { type: XLSX_MIME });
}

/**
 * Entrega el .xlsx. En la app nativa, al visor del sistema (iOS lo abre con
 * QuickLook, con compartir y guardar en Archivos), como el PDF; si eso no se
 * puede ni intentar, y en el navegador, como descarga.
 */
export async function deliverXlsx(blob: Blob, filename: string): Promise<void> {
  if (isDespiaNative() && (await openFileInNativeViewer(blob, filename, XLSX_MIME))) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
